/**
 * @engine/vfx - Variable Nodes
 *
 * Nodes for reading VFX graph variables (exposed parameters).
 * Variables are set from outside the VFX graph via the runtime API.
 *
 * Packing (must match VFXSystemRenderer's variable buffer writes):
 * - 16 floats as 4×float4  → floats[i/4][i%4]
 * - 8 vec2 as 4×float4     → vec2s[i/2].xy / .zw
 * - 8 vec3 padded to float4 → vec3s[i].xyz
 * - 8 colors as float4      → colors[i]
 */

import type { VFXNodeDefinition } from '../types';
import { dataPort } from '../types';

/**
 * Variable node definitions.
 *
 * These nodes read from the variables uniform buffer:
 * - Get Variable (Float) - reads a float variable
 * - Get Variable (Vec2) - reads a vec2 variable
 * - Get Variable (Vec3) - reads a vec3 variable
 * - Get Variable (Color) - reads a vec4 color variable
 */
export const VARIABLE_NODES: readonly VFXNodeDefinition[] = [
  // ============================================================================
  // GET VARIABLE - FLOAT
  // ============================================================================
  {
    id: 'vfx.variable.get_float',
    name: 'Get Variable (Float)',
    category: 'variable',
    contexts: [], // Available in any context
    description: 'Read a float variable from the VFX instance',
    color: '#9C27B0', // Purple for variables
    inputs: [
      dataPort('variableId', 'Variable', 'int', {
        defaultValue: 0,
        range: { min: 0, max: 15 },
      }),
    ],
    outputs: [dataPort('value', 'Value', 'float')],
    shader: {
      // 16 floats packed as 4×float4
      code: `value = vfxVariables.floats[variableId / 4][variableId % 4];`,
    },
    modifiesParticle: false,
  },

  // ============================================================================
  // GET VARIABLE - VEC2
  // ============================================================================
  {
    id: 'vfx.variable.get_vec2',
    name: 'Get Variable (Vec2)',
    category: 'variable',
    contexts: [],
    description: 'Read a vec2 variable from the VFX instance',
    color: '#9C27B0',
    inputs: [
      dataPort('variableId', 'Variable', 'int', {
        defaultValue: 0,
        range: { min: 0, max: 7 },
      }),
    ],
    outputs: [dataPort('value', 'Value', 'vec2')],
    shader: {
      // 8 vec2s packed as 4×float4 (two per slot)
      code: `
        float4 packed = vfxVariables.vec2s[variableId / 2];
        value = (variableId % 2 == 0) ? packed.xy : packed.zw;
      `,
    },
    modifiesParticle: false,
  },

  // ============================================================================
  // GET VARIABLE - VEC3
  // ============================================================================
  {
    id: 'vfx.variable.get_vec3',
    name: 'Get Variable (Vec3)',
    category: 'variable',
    contexts: [],
    description: 'Read a vec3 variable from the VFX instance',
    color: '#9C27B0',
    inputs: [
      dataPort('variableId', 'Variable', 'int', {
        defaultValue: 0,
        range: { min: 0, max: 7 },
      }),
    ],
    outputs: [dataPort('value', 'Value', 'vec3')],
    shader: {
      // vec3s are padded to float4
      code: `value = vfxVariables.vec3s[variableId].xyz;`,
    },
    modifiesParticle: false,
  },

  // ============================================================================
  // GET VARIABLE - COLOR (VEC4)
  // ============================================================================
  {
    id: 'vfx.variable.get_color',
    name: 'Get Variable (Color)',
    category: 'variable',
    contexts: [],
    description: 'Read a color variable from the VFX instance',
    color: '#9C27B0',
    inputs: [
      dataPort('variableId', 'Variable', 'int', {
        defaultValue: 0,
        range: { min: 0, max: 7 },
      }),
    ],
    outputs: [dataPort('value', 'Value', 'color')],
    shader: {
      code: `value = vfxVariables.colors[variableId];`,
    },
    modifiesParticle: false,
  },

  // ============================================================================
  // GET VARIABLE - TEXTURE
  // ============================================================================
  {
    id: 'vfx.variable.get_texture',
    name: 'Get Variable (Texture)',
    category: 'variable',
    contexts: [],
    description: 'Get a texture variable reference from the VFX instance',
    color: '#9C27B0', // Purple (consistent with other variable nodes)
    inputs: [
      dataPort('variableId', 'Variable', 'int', {
        defaultValue: 0,
        range: { min: 0, max: 7 },
      }),
    ],
    outputs: [dataPort('texture', 'Texture', 'texture2d')],
    // Binding provider: the compiler resolves this node to the vfxTexture<N>
    // binding — no shader code.
    modifiesParticle: false,
  },

  // ============================================================================
  // GET VARIABLE - POINT MAP
  // ============================================================================
  {
    id: 'vfx.variable.get_pointmap',
    name: 'Get Variable (Point Map)',
    category: 'variable',
    contexts: [],
    description: 'Get a point map variable reference from the VFX instance',
    color: '#00BCD4', // Cyan for point maps
    inputs: [
      dataPort('variableId', 'Variable', 'int', {
        defaultValue: 0,
        range: { min: 0, max: 7 },
      }),
    ],
    outputs: [dataPort('pointmap', 'Point Map', 'texture2d')],
    // Binding provider: resolved to the vfxTexture<N> binding — no shader code.
    modifiesParticle: false,
  },

  // ============================================================================
  // GET NAMED VARIABLE
  // ============================================================================
  // Syntactic sugar resolved at compile time: the compiler maps the variable
  // name to its packed slot (see CallEmitter.namedVariableAccess).
  {
    id: 'vfx.variable.get',
    name: 'Get Variable',
    category: 'variable',
    contexts: [],
    description: 'Read a named variable from the VFX instance',
    color: '#9C27B0',
    inputs: [
      {
        id: 'variableName',
        name: 'Variable',
        type: 'int', // Actually a variable reference
        kind: 'data',
        defaultValue: '',
        // UI should show a dropdown of available variables
        variableSelector: true,
      },
    ],
    outputs: [dataPort('value', 'Value', 'float')], // Type determined by variable
    modifiesParticle: false,
    // Mark as needing special handling during codegen
    requiresVariableResolution: true,
  },
];
